import { useState } from 'react';
import { SearchX, Mail, ExternalLinkIcon } from 'lucide-react';
import { debounce } from 'lodash';
import { Link } from 'react-router';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { InputSearch } from '@/components/ui/input-search';
import {
  staticIntegrations,
  type IStaticIntegration,
  oauthIntegrations,
  customIntegrations,
  shareFileIntegrations,
} from 'src/shared/entities';
import { renderLogo } from 'src/utils/renderLogo';
import { Button } from '@/components/ui/button';
import env from 'env';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent, CardFooter, CardDescription, CardTitle } from '@/components/ui/card';

import { useIntegration } from '../model';

type Props = {
  dialogTrigger: React.ReactNode;
};

export const IntegrationCard = ({
  provider,
  name,
  description,
  assetSupported,
  depositMethod,
  __typename,
  onClick,
}: IStaticIntegration & {
  onClick: () => void;
}) => {
  const Icon = renderLogo(provider);
  const { activateShareFileIntegration, showSetupAlert } = useIntegration();

  const handleActivate = () => {
    onClick();
    switch (__typename) {
      case 'OauthIntegration':
        showSetupAlert({ type: 'oauth', provider: provider as (typeof oauthIntegrations)[number] });
        break;
      case 'CustomIntegration':
        showSetupAlert({ type: 'custom', provider: provider as (typeof customIntegrations)[number] });
        break;
      case 'ShareFileIntegration':
        activateShareFileIntegration(provider as (typeof shareFileIntegrations)[number]);
        break;
    }
  };

  return (
    <Card
      className="rounded-xl relative cursor-pointer flex flex-col hover:bg-accent transition"
      role="button"
      tabIndex={0}
      onClick={handleActivate}
    >
      <CardHeader className="flex-row justify-between items-center p-5 pb-0 shrink-0">
        <Icon className="size-10" />
        <Badge variant="outline" className="font-semibold px-2 bg-white">
          {depositMethod}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-1.5 px-5 py-4 grow">
        <CardTitle className="font-semibold text-sm">{name}</CardTitle>
        <CardDescription className="text-gray-500 text-xs">{description}</CardDescription>
      </CardContent>
      <CardFooter className="p-5 pt-0 shrink-0">
        <div className="text-xs text-muted-foreground">
          Supported assets: <strong className="font-semibold">{assetSupported}</strong>
        </div>
      </CardFooter>
    </Card>
  );
};

const StaticIntegrationsDialog = ({ dialogTrigger }: Props) => {
  const [integrations, setIntegrations] = useState<IStaticIntegration[]>(staticIntegrations);
  const { isChooseProviderOpen, chooseProvider, resetIntegration } = useIntegration();

  const onSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const filteredIntegrations = staticIntegrations.filter(({ name }) =>
      name.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setIntegrations(filteredIntegrations);
  }, 300);

  const resetIntegrationList = () => setIntegrations(staticIntegrations);

  const onOpenChange = (open: boolean) => {
    if (open) chooseProvider();
    else {
      resetIntegration();
      resetIntegrationList();
    }
  };

  return (
    <Dialog open={isChooseProviderOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      <DialogContent className="grid-rows-[auto_auto_1fr] md:max-w-[1115px] max-h-205 gap-0 p-0">
        <DialogHeader className="sm:flex-row pt-10 px-6 mb-6">
          <div className="grow">
            <DialogTitle className="font-bold mb-2">Choose a provider to set up an integration</DialogTitle>
            <DialogDescription className="mb-3">
              Select a provider below to connect your asset management platform and automatically deposit assets.
            </DialogDescription>
            <InputSearch
              placeholder="Search providers"
              onChange={onSearchChange}
              isLoading={false}
              className="w-full sm:w-[374px]"
              onClear={resetIntegrationList}
            />
          </div>
          <Button asChild variant="secondary" className="shrink-0">
            <Link to="https://codekeeper.co/integrations" target="_blank">
              Learn more <ExternalLinkIcon />
            </Link>
          </Button>
        </DialogHeader>
        <Separator />
        <div className="space-y-12 p-6 overflow-y-auto">
          {integrations.length ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {integrations.map((integration) => (
                <IntegrationCard key={integration.provider} {...integration} onClick={resetIntegrationList} />
              ))}
            </div>
          ) : (
            <EmptyBlock
              title="Integration not found"
              description="If your platform isn't listed, you can upload your assets manually or contact us to discuss other options."
              icon={SearchX}
              withCard={false}
              action={
                <Button size="sm" asChild>
                  <Link to={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`}>
                    <Mail />
                    Contact us
                  </Link>
                </Button>
              }
            />
          )}
          {Boolean(integrations.length) && (
            <p className="text-sm text-muted-foreground font-medium">
              Don&apos;t see the platform you prefer or need a custom integration?{' '}
              <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="underline text-foreground">
                Contact us
              </a>{' '}
              to discuss your options.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaticIntegrationsDialog;
